import { PERCENT_ROUNDING_SCALE } from "@repo/shared";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogCancel,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableSelect, type SearchableSelectOption } from "@/components/ui/searchable-select";
import { useT } from "@/lib/i18n/use-t";
import { useRegionalFormat } from "@/lib/regional-format/context";

import { formatWeightTotal } from "./format-weight-total";
import { T } from "./product-weight-dialog.i18n";

type ProductWeightDialogProps = {
  open: boolean;
  onClose: () => void;
  /** The row whose weight is edited; null adds a product instead. */
  editing: { id: string; name: string; isin: string | null; weight: number } | null;
  /** Products that can still be added. */
  productOptions: SearchableSelectOption[];
  /** Current total of the portfolio's weights, in percent. */
  totalWeight: number;
  /** Step of the weight input. */
  weightStep: string;
  onAdd: (productId: string, weight: number) => Promise<unknown>;
  onUpdate: (rowId: string, weight: number) => Promise<unknown>;
};

/** Adds a product with a weight to a portfolio, or edits the weight of one it holds. */
export function ProductWeightDialog({
  open,
  onClose,
  editing,
  productOptions,
  totalWeight,
  weightStep,
  onAdd,
  onUpdate,
}: ProductWeightDialogProps) {
  const { formatNumber } = useRegionalFormat();
  const t = useT(T);

  const [productId, setProductId] = useState("");
  const [weight, setWeight] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Keyed on the row id: `editing` is a new object on every parent render, and
  // resetting on it would wipe the typed weight and the save error mid-save.
  useEffect(() => {
    if (!open) return;
    setProductId("");
    setWeight(editing ? editing.weight.toString() : "");
    setError(null);
  }, [open, editing?.id]);

  const handleSubmit = async () => {
    setError(null);

    if (editing ? !weight : !productId || !weight) {
      setError(editing ? t.weightRequired : t.selectProductAndWeight);
      return;
    }
    const parsed = parseFloat(weight);
    if (Number.isNaN(parsed) || parsed <= 0 || parsed > 100) {
      setError(t.weightBetween0And100);
      return;
    }

    setIsSaving(true);
    try {
      if (editing) await onUpdate(editing.id, parsed);
      else await onAdd(productId, parsed);
      onClose();
    } catch (err) {
      const fallback = editing ? t.failedToUpdateWeight : t.failedToAddProduct;
      setError((err instanceof Error && err.message) || fallback);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {editing
              ? editing.name
                ? `${t.editProductWeight} (${editing.name})`
                : t.editProductWeight
              : t.addProductToPortfolio}
          </DialogTitle>
          <DialogDescription>{editing ? t.editDescription : t.addDescription}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {error && (
            <div className="text-sm text-destructive bg-destructive/10 p-3 rounded-md">{error}</div>
          )}

          {editing ? (
            <div className="space-y-2">
              <Label>{t.product}</Label>
              <div className="p-3 border rounded bg-muted/50">
                <p className="font-medium">{editing.name}</p>
                <p className="text-sm text-muted-foreground">
                  {t.isin}: {editing.isin}
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="product">{t.product}</Label>
              <SearchableSelect
                id="product"
                value={productId}
                onValueChange={setProductId}
                options={productOptions}
                placeholder={t.selectProduct}
                searchPlaceholder={t.searchProducts}
                emptyMessage={t.noProductsAvailable}
                noResultsMessage={t.noProductsMatch}
              />
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="productWeight">{t.weightPercent}</Label>
            <Input
              id="productWeight"
              type="number"
              step={weightStep}
              min="0"
              max="100"
              value={weight}
              onChange={(e) => setWeight(e.target.value)}
              placeholder={t.weightPlaceholder}
            />
            <p className="text-xs text-muted-foreground">
              {t.currentTotal} {formatWeightTotal(totalWeight, formatNumber)}% | {t.remaining}{" "}
              {formatWeightTotal(
                Math.round((100 - totalWeight) * PERCENT_ROUNDING_SCALE) / PERCENT_ROUNDING_SCALE,
                formatNumber,
              )}
              %
            </p>
          </div>
        </div>

        <DialogFooter>
          <DialogCancel disabled={isSaving}>{t.cancel}</DialogCancel>
          <Button onClick={handleSubmit} loading={isSaving}>
            {editing ? t.save : t.addProduct}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
